import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Search, X } from 'lucide-react';
import { Loader } from '@/components/atoms/Loader';
import { CustomButton } from '@/components/molecules/CustomButton';
import { GenericCard } from '@/components/molecules/GenericCard';
import {
  getPublicUserPokemons,
  type PublicOwnedPokemon,
} from '@/services/pokemon';
import logoUrl from '@/assets/logo.svg';

import { cdnUrl } from '@/utils/cdn';
import { TYPE_COLORS } from '@/utils/pokemonTypes';

const LIMIT = 20;

export const PlayerPC = () => {
  const { username } = useParams<{ username: string }>();

  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [sortBy, setSortBy] = useState<'createdAt' | 'level' | 'nickname' | 'rarity' | 'pokedexId'>(
    'createdAt'
  );
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');
  const [onlyShiny, setOnlyShiny] = useState(false);
  const [onlyLegendary, setOnlyLegendary] = useState(false);
  const [selected, setSelected] = useState<PublicOwnedPokemon | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setOffset(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['playerPC', username, offset, sortBy, sortOrder, debouncedSearch, onlyShiny, onlyLegendary],
    queryFn: () =>
      getPublicUserPokemons(username!, {
        offset,
        limit: LIMIT,
        sortBy,
        sortOrder,
        search: debouncedSearch || undefined,
        onlyShiny,
        onlyLegendary,
      }),
    enabled: !!username,
    retry: (failureCount, err) => {
      if ((err as { status?: number })?.status === 404) return false;
      return failureCount < 2;
    },
  });

  const is404 = (error as { status?: number })?.status === 404;
  const total = data?.total ?? 0;
  const pokemons = data?.data ?? [];
  const totalPages = Math.ceil(total / LIMIT);
  const currentPage = Math.floor(offset / LIMIT) + 1;

  return (
    <div className="bg-page min-h-screen">
      <header className="bg-dark-neutral text-white-neutral shadow-lg">
        <div className="flex items-center px-4 py-3 sm:px-6">
          <img src={logoUrl} alt="Logo Bitume" className="h-8 sm:h-10" />
        </div>
      </header>

      <main className="mx-auto flex w-full max-w-5xl flex-col gap-6 p-5">
        <div className="flex flex-col gap-1">
          <p className="text-content/50 text-sm font-semibold uppercase tracking-wider">
            PC de
          </p>
          <h1 className="text-content font-lexend text-3xl font-bold sm:text-4xl">
            {username}
          </h1>
        </div>

        {is404 && (
          <div className="flex flex-col items-center gap-3 py-16">
            <p className="text-6xl">🔎</p>
            <p className="text-content font-lexend text-xl font-semibold">
              Joueur introuvable
            </p>
            <p className="text-content/50 text-sm">
              Le joueur &laquo;&nbsp;{username}&nbsp;&raquo; n&apos;existe pas.
            </p>
          </div>
        )}

        {error && !is404 && (
          <div className="flex flex-col items-center gap-3 py-16">
            <p className="text-6xl">⚠️</p>
            <p className="text-content font-lexend text-xl font-semibold">
              Erreur de chargement
            </p>
            <p className="text-content/50 text-sm">
              Impossible de charger le PC. Réessaie plus tard.
            </p>
          </div>
        )}

        {!error && (
          <GenericCard
            title="Pokémon"
            subtitle={data ? `${total} Pokémon${total !== 1 ? 's' : ''}` : undefined}
          >
            <div className="mb-2 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <form
                role="search"
                className="relative max-w-xs flex-1"
                onSubmit={(e) => e.preventDefault()}
              >
                <Search
                  size={15}
                  className="text-content/40 pointer-events-none absolute top-1/2 left-3 -translate-y-1/2"
                />
                <input
                  type="text"
                  aria-label="Rechercher un Pokémon"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Rechercher un Pokémon..."
                  className="text-content placeholder:text-content/40 focus:ring-primary-400 h-10 w-full rounded-xl border border-line-soft bg-raised pr-8 pl-9 text-sm focus:ring-2 focus:outline-none"
                />
                {searchInput && (
                  <button
                    type="button"
                    onClick={() => setSearchInput('')}
                    className="text-content/40 hover:text-content/70 absolute top-1/2 right-2.5 -translate-y-1/2 transition-colors"
                    aria-label="Effacer la recherche"
                  >
                    <X size={14} />
                  </button>
                )}
              </form>

              <div className="flex flex-wrap gap-2">
                <select
                  value={sortBy}
                  onChange={(e) => {
                    setSortBy(e.target.value as typeof sortBy);
                    setOffset(0);
                  }}
                  className="text-content h-10 rounded-xl border border-line-soft bg-raised px-3 text-sm focus:outline-none"
                >
                  <option value="createdAt">Date</option>
                  <option value="level">Niveau</option>
                  <option value="nickname">Nom</option>
                  <option value="rarity">Rareté</option>
                  <option value="pokedexId">N° Pokédex</option>
                </select>
                <select
                  value={sortOrder}
                  onChange={(e) => {
                    setSortOrder(e.target.value as typeof sortOrder);
                    setOffset(0);
                  }}
                  className="text-content h-10 rounded-xl border border-line-soft bg-raised px-3 text-sm focus:outline-none"
                >
                  <option value="DESC">↓</option>
                  <option value="ASC">↑</option>
                </select>
                <button
                  type="button"
                  onClick={() => { setOnlyShiny(!onlyShiny); setOffset(0); }}
                  className={`flex h-10 items-center gap-1.5 rounded-xl px-3 text-sm font-semibold transition-colors ${
                    onlyShiny
                      ? 'bg-yellow-400 text-white'
                      : 'border border-line-soft bg-raised text-content/60 hover:bg-line-soft'
                  }`}
                >
                  ✨ Shiny
                </button>
                <button
                  type="button"
                  onClick={() => { setOnlyLegendary(!onlyLegendary); setOffset(0); }}
                  className={`flex h-10 items-center gap-1.5 rounded-xl px-3 text-sm font-semibold transition-colors ${
                    onlyLegendary
                      ? 'bg-amber-500 text-white'
                      : 'border border-line-soft bg-raised text-content/60 hover:bg-line-soft'
                  }`}
                >
                  🟨 Légendaire
                </button>
              </div>
            </div>

            <p className="text-content/50 mb-4 text-xs">
              {data
                ? `${total} Pokémon${total !== 1 ? 's' : ''} trouvé${total !== 1 ? 's' : ''}`
                : '\u00A0'}
            </p>

            {isLoading ? (
              <div className="flex justify-center py-12">
                <Loader size={40} color="#f29602" />
              </div>
            ) : pokemons.length === 0 && !error ? (
              <div className="flex flex-col items-center gap-3 py-12">
                <p className="text-6xl">🔍</p>
                <p className="text-content/50 text-sm">
                  {debouncedSearch
                    ? `Aucun Pokémon trouvé pour «\u00A0${debouncedSearch}\u00A0»`
                    : 'Aucun Pokémon dans ce PC'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                {pokemons.map((publicPokemon) => !publicPokemon.pokemon ? null : (
                  <button
                    key={publicPokemon.ownedPokemonId}
                    onClick={() => setSelected(publicPokemon)}
                    className="hover:border-primary-300 group relative flex flex-col items-center rounded-2xl border border-transparent bg-surface p-3 shadow-sm transition-all duration-200 hover:shadow-lg"
                  >
                    {publicPokemon.isShiny && (
                      <span className="bg-primary-500 absolute top-2 right-2 rounded-full px-1.5 py-0.5 text-[10px] leading-none font-bold text-white">
                        ★
                      </span>
                    )}
                    <div className="bg-primary-500/10 mb-2 flex h-16 w-16 items-center justify-center rounded-xl">
                      <img
                        src={cdnUrl(publicPokemon.isShiny ? publicPokemon.pokemon.spriteShiny : publicPokemon.pokemon.sprite)}
                        alt={publicPokemon.nickname ?? publicPokemon.pokemon.realname}
                        className="h-14 w-14 object-contain transition-transform duration-200 group-hover:scale-110"
                        onError={(e) => { e.currentTarget.style.opacity = '0'; }}
                      />
                    </div>
                    <p className="text-content font-lexend w-full truncate text-center text-sm font-semibold">
                      {publicPokemon.nickname ?? publicPokemon.pokemon.realname}
                    </p>
                    {publicPokemon.nickname && (
                      <p className="text-content/50 mb-1 text-center text-xs">
                        {publicPokemon.pokemon.realname}
                      </p>
                    )}
                    <div className="mb-1.5 flex flex-wrap justify-center gap-1">
                      {publicPokemon.pokemon.types.map((typeName) => (
                        <span
                          key={typeName}
                          className={`${TYPE_COLORS[typeName] ?? 'bg-gray-400'} rounded px-1.5 py-0.5 text-[10px] font-semibold text-white capitalize`}
                        >
                          {typeName}
                        </span>
                      ))}
                    </div>
                    <span className="bg-secondary-500 rounded-full px-2 py-0.5 text-[10px] font-semibold text-white">
                      Nv.&nbsp;{publicPokemon.level}
                    </span>
                  </button>
                ))}
              </div>
            )}

            {totalPages > 1 && (
              <div className="mt-6 flex items-center justify-center gap-3">
                <CustomButton
                  icon={<ChevronLeft size={18} />}
                  iconOnly
                  color="white"
                  rounded="full"
                  disabled={offset === 0}
                  onClick={() => setOffset(Math.max(0, offset - LIMIT))}
                />
                <span className="text-content font-lexend text-sm font-semibold">
                  {currentPage}&nbsp;/&nbsp;{totalPages}
                </span>
                <CustomButton
                  icon={<ChevronRight size={18} />}
                  iconOnly
                  color="white"
                  rounded="full"
                  disabled={offset + LIMIT >= total}
                  onClick={() => setOffset(offset + LIMIT)}
                />
              </div>
            )}
          </GenericCard>
        )}
      </main>

      {selected && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-4 sm:items-center"
          onClick={() => setSelected(null)}
        >
          <div
            className="bg-surface relative w-full max-w-md overflow-y-auto rounded-3xl shadow-2xl"
            style={{ maxHeight: '90dvh' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-secondary-500 sticky top-0 z-10 rounded-t-3xl px-6 pt-6 pb-5">
              <button
                onClick={() => setSelected(null)}
                className="absolute top-4 right-4 text-white/40 transition-colors hover:text-white"
                aria-label="Fermer"
              >
                <X size={20} />
              </button>
              <div className="flex items-center gap-4">
                <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-2xl bg-white/10">
                  <img
                    src={cdnUrl(
                      selected.isShiny
                        ? selected.pokemon.spriteAnimationShiny
                        : selected.pokemon.spriteAnimation
                    )}
                    alt={selected.nickname ?? selected.pokemon.realname}
                    className="h-16 w-16 object-contain"
                    onError={(e) => { e.currentTarget.style.opacity = '0'; }}
                  />
                </div>
                <div>
                  <p className="font-lexend text-xs text-white/40">
                    #{String(selected.pokemon.pokedexId).padStart(3, '0')}
                  </p>
                  <div className="flex items-center gap-2">
                    <h2 className="text-white-neutral font-lexend text-xl font-semibold">
                      {selected.nickname ?? selected.pokemon.realname}
                    </h2>
                    {selected.isShiny && (
                      <span className="text-sm text-yellow-400">★</span>
                    )}
                  </div>
                  {selected.nickname && (
                    <p className="text-sm text-white/60">
                      {selected.pokemon.realname}
                    </p>
                  )}
                  <div className="mt-2 flex gap-1.5">
                    {selected.pokemon.types.map((t) => (
                      <span
                        key={t}
                        className={`${TYPE_COLORS[t] ?? 'bg-gray-400'} rounded px-2 py-0.5 text-xs font-semibold text-white capitalize`}
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6">
              <div className="mb-5 grid grid-cols-3 gap-3 text-center">
                {[
                  { label: 'Niveau', value: selected.level },
                  { label: 'Taille', value: `${selected.pokemon.height} m` },
                  { label: 'Poids', value: `${selected.pokemon.weight} kg` },
                ].map(({ label, value }) => (
                  <div key={label} className="bg-primary-500/10 rounded-xl p-3">
                    <p className="text-content/50 text-xs">{label}</p>
                    <p className="text-content font-lexend font-semibold">
                      {value}
                    </p>
                  </div>
                ))}
              </div>

              {selected.pokemon.description && (
                <div className="bg-secondary-500/10 rounded-2xl p-4">
                  <p className="text-content/70 text-sm leading-relaxed italic">
                    &ldquo;{selected.pokemon.description}&rdquo;
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};